import type { ReactElement } from "react"
import { useMemo } from "react"
import type { Control, FieldPath, FieldValues } from "react-hook-form"
import { useWatch } from "react-hook-form"

import { useDefaultExpenseAccountOptions } from "../hooks/useDefaultExpenseAccountOptions"
import { useResolveGlAccountById } from "../hooks/useResolveGlAccountById"
import { formatGlAccountOptionLabel } from "../utils/formatGlAccountOptionLabel"

import { FormAutocomplete } from "@/components/UiKit/FormControls/FormAutocomplete"
import type { Option } from "@/types/form-fields"

// Local control type — narrow enough to plug into any form whose values
// contain a nullable `defaultExpenseAccountId` field. Accept `undefined` and
// `null` to remain compatible with both required and optional form schemas.
interface DefaultExpenseAccountFormShape extends FieldValues {
  defaultExpenseAccountId?: string | null
}

interface DefaultExpenseAccountSelectorProps<TFieldValues extends DefaultExpenseAccountFormShape> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label: string
  disabled?: boolean
}

export const DefaultExpenseAccountSelector = <TFieldValues extends DefaultExpenseAccountFormShape>({
  control,
  name,
  label,
  disabled,
}: DefaultExpenseAccountSelectorProps<TFieldValues>): ReactElement => {
  const { glAccounts, isFetching, setSearchTerm } = useDefaultExpenseAccountOptions()
  const selectedId = useWatch({ control, name }) as string | null | undefined
  const { glAccount: storedAccount, isFetching: isResolving } = useResolveGlAccountById(selectedId)

  const mergedOptions = useMemo<Option<string>[]>(() => {
    const baseOptions: Option<string>[] = glAccounts.map((option) => ({ value: option.id, label: option.label }))

    if (!storedAccount?.glAccountId) {
      return baseOptions
    }

    const storedId = storedAccount.glAccountId
    const storedIsInactive = !storedAccount.isActive
    const matchIndex = baseOptions.findIndex((option) => option.value === storedId)

    if (matchIndex === -1) {
      const storedLabel = formatGlAccountOptionLabel(
        { number: storedAccount.number, subNumber: storedAccount.subNumber, title: storedAccount.title },
        storedIsInactive
      )
      return [{ value: storedId, label: storedLabel }, ...baseOptions]
    }

    if (storedIsInactive) {
      const relabeled = [...baseOptions]
      const existing = relabeled[matchIndex]
      relabeled[matchIndex] = {
        ...existing,
        label: formatGlAccountOptionLabel(
          { number: storedAccount.number, subNumber: storedAccount.subNumber, title: storedAccount.title },
          true
        ),
      }
      return relabeled
    }

    return baseOptions
  }, [glAccounts, storedAccount])

  return (
    <FormAutocomplete
      control={control}
      name={name}
      label={label}
      options={mergedOptions}
      loading={isFetching || isResolving}
      onInputChange={setSearchTerm}
      disabled={disabled}
      filterOptions={(options) => options}
      data-testid='vendor.accounting.default-expense-account-field'
    />
  )
}
